import { wordsFor, type IssueType } from './issue-types';
/**
 * How a tracker document has to be laid out, and the instructions handed to an
 * assistant that will write one.
 *
 * WHY THIS IS A FILE AND NOT PROSE INSIDE A COMPONENT
 *
 * These instructions are read by something that will act on them exactly. If
 * they drift from what the importer accepts, the result is not an error — it is
 * a document that imports as nothing, silently, and nobody finds out until they
 * notice the board is missing items.
 *
 * That has already happened once. The instructions showed a bug heading with two
 * hashes and stated the heading rule only for issues, so an assistant reasonably
 * generalised, wrote three, and the bug would have vanished without a word.
 *
 * So the examples live here as data, and scripts/test-tracker-format.ts runs
 * every one of them through the importer's own patterns. The instructions cannot
 * describe a shape the importer does not read without something failing.
 */

/** Lines the importer must accept. Every one is checked against its patterns. */
export const VALID_EXAMPLES = [
  '## #12 — Two hashes on an issue',
  '### #12 — Three hashes on an issue',
  '#### #12 — Four hashes on an issue',
  '## B4 — Two hashes on a bug',
  '### B4 — Three hashes on a bug',
  '#### B4 — Four hashes on a bug',
  '### #12 - A hyphen instead of an em dash',
  '### #12 – An en dash',
  '- **Status:** in progress',
  '- **Priority:** high',
  '- **Plain:** One sentence a non-technical reader would understand.',
  '- **Blocked:** What is holding it up.',
  '- **Stands:** Where it currently stands.',
  '- **Epic:** Which group of work this belongs to.',
  '- **Size:** quick',
  '- **Size:** medium — two files, but it needs an on-device measurement.',
  '- **Sprint:** Sprint 4',
  // A sprint is called whatever the team calls it, so the whole line is the
  // name — unlike Priority and Size, where only the leading word is read.
  '- **Sprint:** Hardening fortnight',
  '- **Waiting on:** item #200',
  '- **Waiting on:** test — the load test',
  '- **Waiting on:** person — Pat Moore',
];

/**
 * Ledger rows the parser must accept, checked against parseLedger itself.
 *
 * SEPARATE FROM THE LIST ABOVE because they go through a different parser, and
 * the whole reason this file exists is that instructions checked against the
 * wrong parser are instructions nobody is checking. Every one of these is run
 * through parseLedger in scripts/test-tracker-format.ts, under a Releases
 * heading, and its fields compared against what the row says.
 */
export const VALID_LEDGER_ROWS: { row: string; is: Record<string, unknown> }[] = [
  {
    row: '| 102 | 2026-07-29 | 0.23.0 | #198, #163 | Sign-in works end to end. | the test phone, the office laptop | high |',
    is: {
      build: 102,
      date: '2026-07-29',
      version: '0.23.0',
      items: ['#198', '#163'],
      installed: 'the test phone, the office laptop',
    },
  },
  // No devices column at all — a hosted product, and the ordinary case.
  {
    row: '| 8 | 2026-08-14 | 1.2.0 | #12 | Duplicates stop being copied. |',
    is: { build: 8, version: '1.2.0', items: ['#12'], installed: undefined },
  },
  // An EMPTY devices column, which is a different statement: it went nowhere yet.
  {
    row: '| 9 | 2026-08-15 | 1.2.1 | #13 | A rebuild for the store. |  | high |',
    is: { build: 9, installed: '' },
  },
  // A date worked out afterwards rather than recorded at the time.
  {
    row: '| 61 | ~2026-06-02 | 0.19.0 | #101 | Early build, date reconstructed. | the test phone | low |',
    is: { build: 61, date: '2026-06-02', dateInferred: true, confidence: 'low' },
  },
];

/** Every word the importer understands for Size, smallest first. */
export const SIZE_WORDS = ['quick', 'medium', 'long'];

/**
 * Every kind of thing an item can be waiting on.
 *
 * The kind alone is nearly useless — "a person needs to do something" cannot be
 * acted on, and six items waiting on "a test" may be six different tests. What
 * makes it worth recording is the target beside it: everything waiting on the
 * same thing groups together, and clearing that one thing releases all of them.
 */
export const WAITING_KINDS = ['item', 'person', 'decision', 'external', 'test', 'design', 'bundled', 'parked', 'other'];

/** Every word the importer understands for Status, and what each means. */
export const STATUS_WORDS = ['backlog', 'to do', 'in progress', 'in staging', 'in review', 'done', "won't do"];

export const PRIORITY_WORDS = ['critical', 'high', 'medium', 'low'];

/**
 * Every word the importer understands for Kind.
 *
 * THE FILE HAS TO BE ABLE TO SAY THIS OUT LOUD, and until #195 it could not.
 * Kind was inferred from the heading alone — `#12` a task, `B12` a bug — and it
 * is in FILE_OWNS, so a card set to a feature on the board was written back to
 * `task` by the next push. Silently, because a push reports what it changed and
 * "kind" changing to the value the file implies looks like the file winning,
 * which is what a tracker-owned field is supposed to do.
 *
 * Absent, the heading still decides, so every file written before this line
 * existed keeps the kinds it always had.
 */
export const KIND_WORDS = ['task', 'bug', 'improvement', 'feature'];

/**
 * The instructions, for the one place they are handed over.
 *
 * ⚠️ THE KIND LINE IS NOW THIS BOARD'S, not every board's — #302. A board names
 * its own types, so instructions listing four fixed words would tell an
 * assistant to write something the board would refuse. `types` is the board's
 * own list; left out, the four originals are described exactly as before.
 */
export function trackerInstructions(projectName: string, types: IssueType[] = []): string {
  /*
   * WHAT TO PUT AFTER "Kind:".
   *
   * The four originals are ALWAYS offered, on every board, because they are
   * always accepted — that is Vinnie's answer to what a tracker file means once
   * the vocabulary stops being shared: it never stops being shared. The board's
   * own names come first, because they are what its own people should write.
   */
  const kindWords = types.length ? wordsFor(types) : KIND_WORDS;
  return [
    `# How to write the tracker for ${projectName || 'this project'}`,
    ``,
    `Keep a markdown file of the work — one file, in the project you are working`,
    `on. Each item is a heading followed by fields. The board reads this file`,
    `directly, so the shape matters more than the prose.`,
    ``,
    `If you can read and write files on this machine, keep this file yourself:`,
    `create it if it is missing, and update it as work changes rather than waiting`,
    `to be asked. If you cannot, write the entries out and let a person paste them`,
    `in — the format is the same either way.`,
    ``,
    `## An item of work`,
    ``,
    `### #12 — Short title of the thing`,
    `- **Status:** in progress`,
    `- **Priority:** high`,
    `- **Plain:** One sentence a non-technical reader would understand.`,
    `- **Blocked:** What is holding it up, if anything.`,
    `- **Stands:** Where it currently stands.`,
    `- **Epic:** Which group of work this belongs to.`,
    `- **Size:** quick`,
    `- **Sprint:** Sprint 4`,
    `- **Waiting on:** test — the load test`,
    ``,
    `## A bug`,
    ``,
    `### B4 — Short title of the bug`,
    `- **Priority:** critical`,
    `- **Plain:** One sentence a non-technical reader would understand.`,
    `- **Blocked:** What is holding it up, if anything.`,
    `- **Stands:** Where it currently stands.`,
    ``,
    `## The rules`,
    ``,
    `- Numbers never change and are never reused. #12 means the same item for ever.`,
    `- Issues are "#" and a number. Bugs are "B" and a number. Keep them apart.`,
    `- Headings take two, three or four hashes — "##", "###" or "####". The same`,
    `  choice is allowed for issues and for bugs; use whichever suits the document.`,
    `- After the number comes a space, a dash, and a space. An em dash "—", an en`,
    `  dash "–" and a plain hyphen "-" are all read the same way.`,
    `- **Status:** one of ${STATUS_WORDS.join(', ')}.`,
    `- **Priority:** one of ${PRIORITY_WORDS.join(', ')}.`,
    `- **Kind:** one of ${kindWords.join(', ')}. Leave it out and the heading`,
    `  decides — a "#" item is a task and a "B" item is a bug, as before.`,
    ...(types.length
      ? [
          `  The first few are this board's own names for its types. The last four`,
          `  work on every board, for ever, and land on whichever of this board's`,
          `  types means the same thing — so a file written for one board is never`,
          `  wrong on another.`,
          `  Anything else STOPS the push and says so. Nothing is written, and no`,
          `  card lands as a guess.`,
        ]
      : []),
    `- If an item has NO Status line, the board falls back to the wording of its`,
    `  heading — "✅", "FIXED", "SHIPPED" and "DONE" read as done, "RUNNING" as`,
    `  in progress, "QUEUED" as to do — and strips those words out of the title,`,
    `  because the status column already says it. This is for documents written`,
    `  before this format existed. Write the Status line: it always wins, and`,
    `  guessing from a heading is guessing.`,
    `- **Size:** one of ${SIZE_WORDS.join(', ')} — how much work it is, not how`,
    `  much it matters. Only the first word is read, so the line can carry its`,
    `  reasoning after a dash.`,
    `- **Sprint:** which sprint the item is in, by name, exactly as the board`,
    `  spells it. Only used on boards that work in sprints — leave it out`,
    `  otherwise. THE SPRINT MUST ALREADY EXIST: a name the board does not know`,
    `  is refused and reported, never created. Planning a sprint is something a`,
    `  person does on the board, so a typo here cannot start a fortnight nobody`,
    `  planned. Leave the line out to leave the item where the board has it —`,
    `  an absent Sprint is not an instruction to take it out of one.`,
    `  To take it OUT, say so: **Sprint:** none. A missing line is silence and`,
    `  changes nothing; the word "none" is a sentence and empties the field.`,
    `- **Waiting on:** what the item needs before it can move. Write the kind`,
    `  first — one of ${WAITING_KINDS.join(', ')} — then the specific thing:`,
    `  "test — the load test", "person — Pat Moore", "item #200".`,
    `  **Blocked** says WHY in a sentence; this says WHAT, so the board can group`,
    `  everything waiting on the same thing and show what clearing it would free.`,
    `  For "item" the target must be a reference that exists, or it is a dead link.`,
    `- Every field is optional except the heading. Leave a field out rather than`,
    `  writing "none" or "n/a" — an empty value is read as an answer.`,
    `- **Plain** is for somebody who does not work on this. No jargon, no shorthand.`,
    `  It says what the FEATURE IS, in the words somebody using the app would`,
    `  use — not what problem prompted it and not how it was built. The`,
    `  complaint this answers is a common one: "when I read the cards I have to`,
    `  discern what the features are, and sometimes that takes too long."`,
    `- Under the fields, before any build notes, say the same thing at length:`,
    `  **What you see** on screen, quoting the real words; **What you can do`,
    `  with it**, naming the buttons as the buttons name themselves; and`,
    `  **Where it came from** — who asked and when, so "when did we discuss`,
    `  this" is answered on the card rather than by asking. A card that records`,
    `  a DECISION rather than a feature says **What was decided** and **What it`,
    `  meant for the build** instead; one not built yet says **What it will be**`,
    `  and **What you will be able to do with it**.`,
    `  Migration numbers, test counts and the history of how it was got right`,
    `  still belong on the card — after those, never instead of them.`,
    `- Newest items at the top. The board keeps its own order and does not mind,`,
    `  but people read the file too.`,
    `- Editing an existing item means changing its lines in place. Do not add a`,
    `  second heading with the same number — the first one wins and the newer one`,
    `  is discarded without a word.`,
    ``,
    `Anything the board owns — comments, who it is assigned to, what kind of`,
    `blocker it is — is not in this file and must not be written here. It would be`,
    `overwritten on the next read, silently.`,
    ``,
    `## What has been released`,
    ``,
    `Optional, and plenty of projects will not want it. Keep a table under a`,
    `heading that says **Releases** — one row per version that actually went out.`,
    `Leave the whole section out if nothing has been released yet; an absent`,
    `section says nothing, and is never read as "this project has released`,
    `nothing".`,
    ``,
    `## Releases`,
    ``,
    `| Build | Date | Version | What went out | In one sentence | Installed on | Confidence |`,
    `|---|---|---|---|---|---|---|`,
    `| 8 | 2026-08-15 | 1.2.0 | #12, #14 | Sign-in works end to end. | the test phone, the office laptop | high |`,
    `| 7 | 2026-08-01 | 1.1.0 | #9 | Duplicates stop being copied. | the test phone | high |`,
    ``,
    `- The **build number** counts every build ever made and never repeats. It is`,
    `  how a release is identified, so it is the one column that cannot be left out.`,
    `- **Date** is the day it went out, as YYYY-MM-DD. Put a "~" in front of a date`,
    `  you worked out afterwards rather than recorded at the time — the board shows`,
    `  it as approximate rather than passing a guess off as a record.`,
    `- **Version** is what the release is called. Several builds usually share one.`,
    `- **What went out** is the item numbers it carried, separated by commas.`,
    `- **In one sentence** is for somebody who does not work on this. No jargon.`,
    `- **Confidence** is "high", or "low" for a row pieced together afterwards from`,
    `  scattered notes rather than written down when it shipped.`,
    ``,
    `### Installed on — leave the column out unless the project ships to devices`,
    ``,
    `Most do not. A website or a hosted service is RELEASED, not installed: there`,
    `is nothing to name, and a row that names nothing is the correct row. Leave the`,
    `column out entirely and the board says nothing about devices.`,
    ``,
    `If the project does install onto things — phones, laptops, test machines —`,
    `name what you actually put it on, separated by commas. Then three rules, and`,
    `the second is the one that is easy to get wrong:`,
    ``,
    `- **No column at all means "I am not saying".** The board keeps whatever it`,
    `  has. This is what makes it safe to add the column later, or to leave it off`,
    `  a row you did not witness.`,
    `- **An empty column means "nowhere yet".** Different from leaving it out, and`,
    `  it is a claim: it says the release exists and has not been installed. Only`,
    `  write it when that is true.`,
    `- **You own only what you write here.** People add devices on the board`,
    `  afterwards — a laptop done by hand the next day, a phone that updated itself`,
    `  overnight — and nothing you write removes those. Equally, the names in this`,
    `  column are restated on every push, so correcting a wrong device means`,
    `  correcting this row. Taking it off the board alone will not hold.`,
    ``,
    `Do not invent devices. If you did not install it, do not name it — this column`,
    `is read as a statement about the world, and somebody decides what to test on`,
    `by looking at it.`,
    ``,
    `Everything else about a release belongs to the board and must not be written`,
    `here: whether it was withdrawn or rolled back, and which sprint it was cut at`,
    `the close of. Those are recorded by a person and are left alone by a push.`,
    ``,
    `## Comments are replies, not progress reports`,
    ``,
    `Progress goes in the item's own fields. **Stands** is where the current`,
    `position lives, and rewriting it as things change IS reporting progress —`,
    `everyone reading the board sees it without opening anything.`,
    ``,
    `A comment is a reply to a person. Write one when somebody has asked something,`,
    `and answer what they asked. Do not post an update because there is an update:`,
    `a card whose story is told across nine comments has to be read backwards to`,
    `find out where it actually stands, and the fields — the part people scan —`,
    `say nothing.`,
    ``,
    `The test is simple. If nobody asked, put it in **Stands**. If somebody asked,`,
    `reply to them. If the answer to their question is also the new state of the`,
    `work, do both: update **Stands** first, then reply briefly and point at it.`,
  ].join('\n');
}
